import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, GraduationCap, CheckCircle2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NearbySchools from "@/components/NearbySchools";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Apply() {
  const { examId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [notes, setNotes] = useState("");
  const [consents, setConsents] = useState({ kvkk: false, communication: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState("");

  useEffect(() => {
    api.get(`${V1}/scholarship-exams/${examId}`).then((r) => setExam(r.data)).catch(() => {});
    if (user?.role === "parent") {
      api.get(`${V1}/children`).then((r) => setChildren(r.data || []));
    }
  }, [examId, user]);

  useEffect(() => {
    if (!exam?.school_id) return;
    api.get(`${V1}/schools/${exam.school_id}/campuses`).then((r) => {
      const list = r.data || [];
      setCampuses(list);
      if (list.length === 1) setCampusId(list[0].id);
    }).catch(() => setCampuses([]));
  }, [exam?.school_id]);

  const submit = async () => {
    if (!childId) return toast.error("Bir çocuk seçin");
    if (campuses.length > 1 && !campusId) return toast.error("Başvurmak istediğiniz kampüsü seçin");
    if (!consents.kvkk) return toast.error("KVKK onayı gereklidir");
    setSubmitting(true);
    try {
      const { data } = await api.post(`${V1}/scholarship-applications`, {
        child_id: childId,
        scholarship_exam_id: examId,
        campus_id: campusId || null,
        consents,
        notes,
      });
      setDone(data);
      toast.success("Başvuru alındı!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Başvuru gönderilemedi");
    } finally { setSubmitting(false); }
  };

  if (!exam) return <div className="p-16 text-center text-muted-foreground">Yükleniyor…</div>;
  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="apply-success">
        <CheckCircle2 className="h-16 w-16 text-accent mx-auto mb-4" />
        <h1 className="font-display text-3xl font-bold">Başvurun alındı!</h1>
        <div className="text-muted-foreground mt-2">Başvuru numarası: <b className="text-foreground">{done.application_no}</b></div>
        <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto">Okul incelemeyi tamamladığında bildirim alacaksın. Başvuru durumunu panelinden takip edebilirsin.</p>
        <Button asChild className="mt-8"><Link to="/panel/basvurularim">Başvurularım</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid="apply-page">
      <div className="rounded-xl border border-border bg-card p-6 mb-6">
        <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <MapPin className="h-3 w-3" /> {exam.school?.province} / {exam.school?.district}
        </div>
        <div className="font-display font-bold text-2xl">{exam.title}</div>
        <div className="text-sm text-muted-foreground mt-1">{exam.school?.name}</div>
        <div className="text-sm text-muted-foreground mt-3 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(exam.exam_date).toLocaleDateString("tr-TR")}</span>
          <span>Sınıflar: {(exam.grade_levels || []).join(", ")}</span>
          <span>Kontenjan: {exam.quota}</span>
        </div>
        {exam.conditions && <div className="mt-4 p-3 rounded bg-muted text-sm">{exam.conditions}</div>}
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div>
          <div className="overline text-accent mb-1">Adım 1</div>
          <div className="font-display font-bold text-lg">Öğrenci Seçimi</div>
        </div>
        {children.length === 0 ? (
          <div className="p-4 rounded border border-dashed border-border text-sm text-muted-foreground">
            Henüz çocuk profili eklemediniz. <Link to="/panel/cocuklarim" className="text-primary underline">Şimdi ekle</Link>
          </div>
        ) : (
          <Select value={childId} onValueChange={setChildId}>
            <SelectTrigger data-testid="apply-child-select"><SelectValue placeholder="Çocuk seçin" /></SelectTrigger>
            <SelectContent>
              {children.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name} {c.surname} · {c.grade_level ? `${c.grade_level}. sınıf` : "sınıf yok"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {campuses.length > 1 && (
          <div>
            <Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Kampüs Seçimi</Label>
            <p className="text-xs text-muted-foreground mb-1.5">Bu okulun birden fazla yerleşkesi var. Başvurunun hangi kampüs için değerlendirileceğini seçin.</p>
            <Select value={campusId} onValueChange={setCampusId}>
              <SelectTrigger data-testid="apply-campus-select"><SelectValue placeholder="Kampüs seçin" /></SelectTrigger>
              <SelectContent>
                {campuses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} {c.district ? `· ${c.district}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {campuses.length === 1 && (
          <div className="p-3 rounded-lg border border-border bg-muted/40 text-sm flex items-center gap-2" data-testid="apply-single-campus-info">
            <Building2 className="h-4 w-4 text-accent" />
            Başvurun <b>{campuses[0].name}</b> kampüsü için değerlendirilecek.
          </div>
        )}
        <div>
          <Label>Notlar (opsiyonel)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Okula iletmek istediğiniz kısa bir not…" />
        </div>

        <div className="space-y-3 pt-2 border-t border-border">
          <div className="overline text-accent">Adım 2 · Onaylar</div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={consents.kvkk} onCheckedChange={(v) => setConsents((s) => ({ ...s, kvkk: !!v }))} data-testid="consent-kvkk" />
            <span>KVKK aydınlatma metnini okudum, çocuğuma ait bilgilerin bursluluk başvurusu kapsamında ilgili okulla paylaşılmasına onay veriyorum.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={consents.communication} onCheckedChange={(v) => setConsents((s) => ({ ...s, communication: !!v }))} data-testid="consent-comm" />
            <span>Okulun benimle bursluluk süreci hakkında iletişime geçmesine onay veriyorum (SMS, e-posta, telefon).</span>
          </label>
        </div>

        <Button onClick={submit} disabled={submitting || !childId} className="w-full" size="lg" data-testid="apply-submit">
          {submitting ? "Gönderiliyor…" : "Başvuruyu Gönder"}
        </Button>
      </div>

      {exam.school_id && (
        <div className="mt-10" data-testid="apply-nearby-info">
          <p className="text-sm text-muted-foreground mb-3">
            Bu bursluluk başvurusu yalnızca <b>{exam.school?.name}</b> için geçerlidir. Şansını artırmak
            istersen bölgedeki yakın/benzer okullara da ayrıca ve bağımsız olarak başvuru yapabilirsin:
          </p>
          <NearbySchools schoolId={exam.school_id} limit={4} />
        </div>
      )}
    </div>
  );
}
